/**
 * Frontend error reporter — ships browser errors to the backend so they land
 * in system_logs (category FRONTEND) instead of vanishing with the tab.
 *
 * Transport is a bare fetch, NOT the shared axios instance: the axios
 * interceptors acquire MSAL tokens and can redirect to login on failure —
 * exactly the machinery that may be broken when we're reporting a crash.
 * The endpoint is auth-free by design and rate-limited server-side.
 */
import { config } from '../config/config';

export type ClientErrorSource = 'window.onerror' | 'unhandledrejection' | 'error-boundary';

export interface ClientErrorInput {
  message: string;
  stack?: string;
  source: ClientErrorSource;
}

// One report per unique (source, message) per window; hard cap across all
// errors so a render/reject loop can't firehose the backend.
const DEDUPE_WINDOW_MS = 5 * 60 * 1000;
const MAX_REPORTS_PER_MINUTE = 10;

const NBSP = String.fromCharCode(0xa0);

let lastSentBySignature = new Map<string, number>();
let sentTimestamps: number[] = [];
let userEmailProvider: (() => string | undefined) | null = null;

/** Lets the app supply the signed-in user's email once MSAL is up. */
export function setErrorReporterUser(provider: () => string | undefined): void {
  userEmailProvider = provider;
}

/**
 * The Azure App Gateway WAF (OWASP CRS rule 932150) blocks any JSON string
 * value starting with uppercase "GET " — it pattern-matches the unix
 * `GET` binary. Fetch-failure messages and stacks routinely start that way,
 * so swap the space for a non-breaking space; readable, but no longer a match.
 */
export function sanitizeForWaf(value: string): string {
  return value.startsWith('GET ') ? `GET${NBSP}` + value.slice(4) : value;
}

/**
 * Browser notices that reach window.onerror with no Error object and no stack.
 * Nothing threw and nothing broke: per spec, a ResizeObserver callback that
 * resizes its own observation target has the remaining notifications deferred
 * to the next frame, and this is the browser saying so. Any virtualized grid
 * emits them (TanStack Virtual observes the scroll element), so reporting them
 * just buries real crashes in system_logs. They cannot be caught by try/catch
 * or an ErrorBoundary either — filtering here is the only place.
 */
const IGNORED_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

function isBenignBrowserNotice(message: string): boolean {
  return IGNORED_MESSAGES.some((ignored) => message.includes(ignored));
}

export function reportClientError(input: ClientErrorInput): void {
  try {
    // Checked before the dedupe/rate-limit bookkeeping so a page emitting these
    // every frame can't spend the per-minute budget a real crash needs.
    if (isBenignBrowserNotice(input.message)) {
      return;
    }

    const now = Date.now();
    const signature = `${input.source}|${input.message.slice(0, 200)}`;

    const lastSent = lastSentBySignature.get(signature);
    if (lastSent !== undefined && now - lastSent < DEDUPE_WINDOW_MS) {
      return;
    }

    sentTimestamps = sentTimestamps.filter((t) => now - t < 60 * 1000);
    if (sentTimestamps.length >= MAX_REPORTS_PER_MINUTE) {
      return;
    }

    lastSentBySignature.set(signature, now);
    sentTimestamps.push(now);

    const body = JSON.stringify({
      message: sanitizeForWaf(input.message),
      stack: input.stack ? sanitizeForWaf(input.stack) : undefined,
      source: input.source,
      page_url: window.location.href,
      user_email: userEmailProvider?.(),
      user_agent: navigator.userAgent,
    });

    fetch(`${config.API_URL}/client-errors/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      // keepalive lets the report survive a page unload/reload right after a crash
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Reporting an error must never itself throw
  }
}

export function initErrorReporter(): void {
  window.addEventListener('error', (event: ErrorEvent) => {
    reportClientError({
      message: event.message || String(event.error ?? 'Unknown window error'),
      stack: event.error?.stack,
      source: 'window.onerror',
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason: unknown = event.reason;
    const err = reason instanceof Error ? reason : undefined;
    reportClientError({
      message: err?.message ?? String(reason ?? 'Unhandled promise rejection'),
      stack: err?.stack,
      source: 'unhandledrejection',
    });
  });
}

export function _resetErrorReporterForTests(): void {
  lastSentBySignature = new Map();
  sentTimestamps = [];
  userEmailProvider = null;
}
